/**
 * Time utility functions for Auto Fleet Generator
 * 
 * Provides time parsing, formatting, and calculation utilities specifically
 * for shift generation and fleet scheduling. Reuses existing dateTimeUtils
 * where appropriate and extends with shift-specific functionality.
 */

import {
  isValidTimeFormat,
  timeToMinutes,
  minutesToTime,
} from '../../../../utils/dateTimeUtils';

// Re-export commonly used functions from dateTimeUtils
export { isValidTimeFormat, timeToMinutes, minutesToTime };

/**
 * Parse time string (HH:MM) to minutes from midnight
 * Alias for timeToMinutes for consistency with Python implementation
 * 
 * @param timeStr - Time string in HH:MM format (e.g., "06:00", "14:30")
 * @returns Minutes from midnight (0-1439)
 * @throws Error if time format is invalid
 * 
 * @example
 * parseTime("06:00") // returns 360
 * parseTime("14:30") // returns 870
 * parseTime("23:59") // returns 1439
 */
export function parseTime(timeStr: string): number {
  return timeToMinutes(timeStr);
}

/**
 * Format minutes from midnight to HH:MM string
 * Alias for minutesToTime for consistency with Python implementation
 * 
 * @param minutes - Minutes from midnight (0-1439)
 * @returns Time string in HH:MM format
 * @throws Error if minutes are out of valid range
 * 
 * @example
 * formatTime(360) // returns "06:00"
 * formatTime(870) // returns "14:30"
 * formatTime(1439) // returns "23:59"
 */
export function formatTime(minutes: number): string {
  return minutesToTime(minutes);
}

/**
 * Round time in minutes to the nearest 15-minute interval
 * Used to create cleaner shift start/end times
 * 
 * @param minutes - Time in minutes from midnight
 * @returns Rounded time in minutes
 * 
 * @example
 * roundToNearest15(362) // returns 360 (06:00)
 * roundToNearest15(368) // returns 375 (06:15)
 * roundToNearest15(922) // returns 915 (15:15)
 */
export function roundToNearest15(minutes: number): number {
  return Math.round(minutes / 15) * 15;
}

/**
 * Round time in minutes to the nearest 30-minute interval
 * Alternative rounding for less granular scheduling
 * 
 * @param minutes - Time in minutes from midnight
 * @returns Rounded time in minutes
 * 
 * @example
 * roundToNearest30(350) // returns 360 (06:00)
 * roundToNearest30(380) // returns 390 (06:30)
 */
export function roundToNearest30(minutes: number): number {
  return Math.round(minutes / 30) * 30;
}

/**
 * Calculate time difference between start and end times
 * Handles overnight periods (when end < start)
 * 
 * @param startMinutes - Start time in minutes from midnight
 * @param endMinutes - End time in minutes from midnight
 * @returns Duration in minutes (always positive)
 * 
 * @example
 * calculateTimeDifference(360, 480) // returns 120 (2 hours)
 * calculateTimeDifference(1380, 360) // returns 420 (7 hours overnight: 23:00 to 06:00)
 */
export function calculateTimeDifference(startMinutes: number, endMinutes: number): number {
  if (endMinutes >= startMinutes) {
    // Same-day service
    return endMinutes - startMinutes;
  } else {
    // Overnight service (crosses midnight)
    return (1440 - startMinutes) + endMinutes;
  }
}

/**
 * Add minutes to a time, handling day overflow
 * 
 * @param startMinutes - Starting time in minutes from midnight
 * @param durationMinutes - Duration to add in minutes
 * @returns New time in minutes, wrapped to 0-1439 range
 * 
 * @example
 * addMinutes(360, 480) // returns 840 (06:00 + 8 hours = 14:00)
 * addMinutes(1380, 120) // returns 60 (23:00 + 2 hours = 01:00 next day)
 */
export function addMinutes(startMinutes: number, durationMinutes: number): number {
  return (startMinutes + durationMinutes) % 1440;
}

/**
 * Check if a time falls within a time range
 * Handles ranges that cross midnight
 * 
 * @param timeMinutes - Time to check in minutes from midnight
 * @param rangeStart - Range start in minutes from midnight
 * @param rangeEnd - Range end in minutes from midnight
 * @returns True if time is within range
 * 
 * @example
 * isTimeInRange(420, 360, 480) // returns true (07:00 is between 06:00 and 08:00)
 * isTimeInRange(60, 1380, 120) // returns true (01:00 is in overnight range 23:00-02:00)
 * isTimeInRange(600, 360, 480) // returns false (10:00 is not between 06:00 and 08:00)
 */
export function isTimeInRange(timeMinutes: number, rangeStart: number, rangeEnd: number): boolean {
  if (rangeEnd >= rangeStart) {
    // Same-day range
    return timeMinutes >= rangeStart && timeMinutes <= rangeEnd;
  } else {
    // Overnight range (crosses midnight)
    return timeMinutes >= rangeStart || timeMinutes <= rangeEnd;
  }
}

/**
 * Convert time string to minutes with validation
 * More forgiving than parseTime - handles various formats
 * 
 * @param timeStr - Time string in various formats
 * @returns Minutes from midnight, or null if invalid
 * 
 * @example
 * parseTimeForgiving("6:00") // returns 360
 * parseTimeForgiving("06:00") // returns 360
 * parseTimeForgiving("invalid") // returns null
 */
export function parseTimeForgiving(timeStr: string): number | null {
  if (!timeStr || typeof timeStr !== 'string') {
    return null;
  }

  // Try to extract time components
  const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) {
    return null;
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

/**
 * Get a list of time labels for timeline visualization
 * 
 * @param startMinutes - Start time in minutes from midnight
 * @param endMinutes - End time in minutes from midnight
 * @param intervalMinutes - Interval between labels (e.g., 30, 60)
 * @returns Array of time labels in HH:MM format
 * 
 * @example
 * getTimeLabels(360, 720, 60) // returns ["06:00", "07:00", "08:00", ..., "12:00"]
 */
export function getTimeLabels(
  startMinutes: number,
  endMinutes: number,
  intervalMinutes: number = 60
): string[] {
  const labels: string[] = [];
  const duration = calculateTimeDifference(startMinutes, endMinutes);
  
  for (let i = 0; i <= duration; i += intervalMinutes) {
    const currentTime = addMinutes(startMinutes, i);
    labels.push(formatTime(currentTime));
  }
  
  return labels;
}

/**
 * Format a time duration as human-readable string
 * 
 * @param minutes - Duration in minutes
 * @returns Formatted string (e.g., "2h 30m", "45m")
 * 
 * @example
 * formatDuration(150) // returns "2h 30m"
 * formatDuration(45) // returns "45m"
 * formatDuration(480) // returns "8h"
 */
export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  if (hours === 0) {
    return `${mins}m`;
  } else if (mins === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${mins}m`;
  }
}

/**
 * Validate that end time is after start time
 * For overnight services, returns true if end < start (crosses midnight)
 * 
 * @param startTime - Start time in HH:MM format
 * @param endTime - End time in HH:MM format
 * @param allowOvernight - Whether to allow overnight shifts
 * @returns True if valid
 * 
 * @example
 * validateTimeOrder("06:00", "14:00", false) // returns true
 * validateTimeOrder("22:00", "06:00", true) // returns true (overnight)
 * validateTimeOrder("22:00", "06:00", false) // returns false (end before start)
 */
export function validateTimeOrder(
  startTime: string,
  endTime: string,
  allowOvernight: boolean = true
): boolean {
  if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
    return false;
  }
  
  const startMinutes = parseTime(startTime);
  const endMinutes = parseTime(endTime);
  
  if (endMinutes > startMinutes) {
    return true; // Normal same-day service
  } else if (endMinutes < startMinutes && allowOvernight) {
    return true; // Overnight service
  } else {
    return false; // Same time or invalid
  }
}










